import React from "react";
import { Input, Checkbox, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getB2BCustomers } from "../../../../services/index";

const B2BSearchBar = ({
  lastSearchedValue,
  setLastSearchedValue,
  isViewMode,
  customerConfigData,
  searchByName,
  setSearchByName,
  b2bLoading,
  searchPending,
  b2bSearchResults,
  setB2bSearchResults,
  selectedB2BCustomer,
  setSelectedB2BCustomer,
  showB2BDropdown,
  setShowB2BDropdown,
  searchB2BCustomers,
  mapB2BDataDynamic,
  groupedFields,
  activeTab,
  customerForm,
  setFormDataCache,
  setInvoiceAddressList,
  setDeliveryAddressList,
  setSelectedInvoiceAddress,
  setSelectedDeliveryAddress,
  setLoading,
  validateGSTNumber,
  mapGSTToFormValuesDynamic,
  gstInfo,
  setGstInfo,
  setSwitchToggle,
  copyInvoiceToDelivery,
  isPanCustomer,
  isB2BEnabled,
}) => {
  return (
    <div style={{ position: "relative", width: "60%" }}>
      <Input
        placeholder={searchByName ? "Search B2B Customers by Name..." : "Search B2B Customers by GST No..."}
        allowClear
        value={lastSearchedValue}
        disabled={isViewMode || !customerConfigData?.b2bSettings?.enableB2BCustomerRegistration}
        onKeyDown={async (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            if (b2bLoading || searchPending) return;
            if (!searchByName && lastSearchedValue.trim()) {
              try {
                setLoading(true);
                const gstResponse = await validateGSTNumber(lastSearchedValue.trim());
                if (gstResponse?.valid) {
                  if (setGstInfo) setGstInfo(gstResponse);
                  const mappedValues = mapGSTToFormValuesDynamic(gstResponse, groupedFields);
                  setSwitchToggle(true);
                  setTimeout(() => {
                    copyInvoiceToDelivery();
                  }, 0);
                  setB2bSearchResults([]);
                  setShowB2BDropdown(false);

                  // BUG-2113 (RC2): see identical fix + comment in
                  // B2BCustomerSearch.js's handleGSTEnter — validateGSTNumber
                  // only checks the external GST API, so b2bSearchResults[0]
                  // (from the unrelated debounced keystroke search) is not a
                  // reliable stand-in for "the existing customer this GST
                  // belongs to." Look it up explicitly instead.
                  let matchedCustomer = null;
                  try {
                    const gstNo = lastSearchedValue.trim().toUpperCase();
                    const lookup = await getB2BCustomers({ q: gstNo, startRow: "0", endRow: "20" });
                    const candidates = lookup?.data?.getB2BCustomerList?.customers || [];
                    matchedCustomer = candidates.find((c) => c.gstNo?.toUpperCase() === gstNo) || candidates[0] || null;
                  } catch (lookupErr) {
                    console.error("B2B customer lookup by GST failed", lookupErr);
                  }

                  // BUG-2156: RC1's original fix (below, until this commit) also wrote
                  // name/customerName/editName here -- unscoped, straight onto the
                  // Contact tab's real "Customer Name" field, the instant Enter was
                  // pressed on this Business-tab GST input. That silently overwrote/
                  // populated the Contact tab while the user was still just entering
                  // GST details, which is BUG-2156. The Contact-tab name still needs to
                  // fall back to the B2B identity so BUG-2113's required-field gate
                  // doesn't force the user to type an unrelated name -- but that fallback
                  // now happens once, at submit time, in CustomerDrawerFooter's
                  // handleSubmit (only when the Contact tab's name is still empty), not
                  // proactively here on every keystroke/selection.
                  customerForm.setFieldsValue({ ...mappedValues });
                  setFormDataCache((prev) => ({
                    ...prev,
                    [activeTab]: { ...(prev[activeTab] || {}), ...mappedValues },
                  }));

                  if (matchedCustomer) {
                    setSelectedB2BCustomer(matchedCustomer);
                    if (setInvoiceAddressList) setInvoiceAddressList(matchedCustomer.invoiceAddresses || []);
                    if (setDeliveryAddressList) setDeliveryAddressList(matchedCustomer.deliveryAddresses || []);
                  } else {
                    setSelectedB2BCustomer(null);
                  }
                }
              } catch (err) {
                console.error("GST validation failed", err);
              } finally {
                setLoading(false);
              }
            }
          }
        }}
        onChange={(e) => {
          const v = e.target.value;
          setLastSearchedValue(v);
          setSelectedB2BCustomer(null);
          if (setGstInfo) setGstInfo(null);
          if (v.trim() !== "") {
            setShowB2BDropdown(true);
            searchB2BCustomers(v);
          } else {
            searchB2BCustomers("");
            setShowB2BDropdown(false);
            customerForm.resetFields();
            setFormDataCache((prev) => ({ ...prev, [activeTab]: {} }));
          }
        }}
        onClear={() => {
          setLastSearchedValue("");
          searchB2BCustomers("");
          setSelectedB2BCustomer(null);
          if (setGstInfo) setGstInfo(null);
          customerForm.resetFields();
          setFormDataCache((prev) => ({ ...prev, [activeTab]: {} }));
        }}
        loading={b2bLoading}
        prefix={<SearchOutlined style={{ color: "#a0a0a0" }} />}
        style={{
          height: "30px",
          borderRadius: "6px",
          fontSize: "14px",
          width: "65%",
          backgroundColor: !customerConfigData?.b2bSettings?.enableB2BCustomerRegistration ? "#f5f5f5" : "#fff",
          cursor: !customerConfigData?.b2bSettings?.enableB2BCustomerRegistration ? "not-allowed" : "text",
        }}
      />

      <Checkbox
        checked={searchByName}
        style={{ marginLeft: "1vw" }}
        onChange={(e) => {
          setSearchByName(e.target.checked);
          setB2bSearchResults([]);
          setSelectedB2BCustomer(null);
          if (setGstInfo) setGstInfo(null);
          customerForm.resetFields();
          setFormDataCache((prev) => ({ ...prev, [activeTab]: {} }));
        }}
        disabled={isViewMode}
      >
        Search by Name
      </Checkbox>

      {showB2BDropdown && lastSearchedValue?.trim() !== "" && !selectedB2BCustomer && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: "300px",
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            marginTop: "4px",
            zIndex: 10,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "8px 0",
            width: "65%",
          }}
        >
          {b2bLoading && (
            <div style={{ textAlign: "center", padding: "12px" }}>
              <Spin size="small" /> Searching…
            </div>
          )}

          {!b2bLoading && b2bSearchResults.length > 0 && (
            <>
              {b2bSearchResults.map((customer) => (
                <div
                  key={customer.customerId}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                  onClick={async () => {
                    setB2bSearchResults([]);
                    setLastSearchedValue(customer.customerName);
                    setSelectedB2BCustomer(customer);
                    setSwitchToggle(true);
                    setTimeout(() => {
                      copyInvoiceToDelivery();
                    }, 0);
                    setInvoiceAddressList(customer.invoiceAddresses || []);
                    setDeliveryAddressList(customer.deliveryAddresses || []);
                    setSelectedInvoiceAddress(null);
                    setSelectedDeliveryAddress(null);
                    const mappedValues = mapB2BDataDynamic(customer, groupedFields);
                    const currentValues = customerForm.getFieldsValue();
                    const b2bFields =
                      isPanCustomer && isB2BEnabled
                        ? {
                            b2bCustomerId: customer.customerId,
                            b2bCustomerName: customer.customerName,
                            b2bCustomerCode: customer.customerCode,
                          }
                        : {};

                    // BUG-2156: see comment above in the GST-Enter handler -- identity
                    // fields (name/customerName/editName) are no longer written
                    // proactively here; CustomerDrawerFooter's submit-time fallback
                    // covers the Contact-tab name requirement instead.
                    customerForm.setFieldsValue({
                      ...currentValues,
                      ...mappedValues,
                      ...b2bFields,
                    });
                    setFormDataCache((prev) => ({
                      ...prev,
                      [activeTab]: {
                        ...(prev[activeTab] || {}),
                        ...mappedValues,
                        ...b2bFields,
                      },
                    }));
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: "15px", color: "#1a1a1a" }}>{customer.customerName}</div>
                  <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>
                    <strong>Code:</strong> {customer.customerCode}
                  </div>
                </div>
              ))}
            </>
          )}

          {!b2bLoading && lastSearchedValue?.trim() !== "" && b2bSearchResults.length === 0 && (
            <div style={{ textAlign: "center", padding: "12px", color: "#999" }}>No customers found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default B2BSearchBar;
